/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: BI Dashboard
 *| (C) Copyright IBM Corp. 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([
	'./VisQueryPostProcessor'
], function(VisQueryPostprocessor) {
	'use strict';

	/**
	 * This module applies a user's sort overrides to the data items. For example,
	 * A column can have the members A, B, C, D, E and F in ascending order. If a
	 * user overrides the default ascending sort order by forcing C and D to the top,
	 * this module will post process the query result for the order to be applied.
	 * At the end of the post process, the order will be C, D, A, B, E and F.
	 *
	 **/
	return class PostprocessorClass extends VisQueryPostprocessor {

		/**
		 * @Constructor
		 * @param {Object} options
		 */
		init(options) {
			super.init(options);
			this.visualizationApi = options.visualizationApi;
			this.visDataItems = this.visualizationApi.getSlots().getDataItemList() || [];
			this.data = this._queryResultData.data;
			this.dataItems = this._queryResultData.dataItems;
		}

		/**
		 * Apply the users overrides
		 * @return {QueryResultData}
		 */
		_processData() {
			this.dataItemsMapping = {};
			this.visDataItems.forEach((visDataItem) => {
				this.dataItemsMapping[visDataItem.getId()] = visDataItem;
			});

			let doesSortExist = false;
			this.dataItems.forEach((dataItem, dataItemIndex) => {
				if (dataItem.items && dataItem.items.length) {
					// given the data item, see if there are any custom sorts/overrides
					let topOverrides =  this._getOverrides(dataItem);

					//At least one custom sort exists within this dataItem itemClass
					if (this._findIndex(topOverrides, (topOverride) => topOverride.length) !== -1) {
						doesSortExist = true;
						//Update data items with the new sort, given the overrides
						dataItem.items = this._updateDataItemWithOverrides(dataItem, dataItemIndex, topOverrides);
					}
				}
			});

			// Sort data by indices if there exists categorical data.
			// Sort priority is determined by dataItem index
			if (doesSortExist === true) {
				this._queryResultData.data.sort((a,b) => {
					for (let i = 0; i < this.visDataItems.length; i++) {
						const aPt = a.pt[i];
						const bPt = b.pt[i];
						if (!isNaN(aPt) && !isNaN(bPt) && aPt !== bPt) {
							return aPt < bPt ? -1 : 1;
						} else if (!isNaN(aPt.v) && !isNaN(bPt.v) && aPt.v !== bPt.v) {
							const sort = this.visDataItems[i].getSort();
							if (sort && sort.type=== 'desc') {
								return aPt.v < bPt.v ? 1 : -1;
							} else if (sort && sort.type=== 'asc') {
								return aPt.v < bPt.v ? -1 : 1;
							}
						}
					}
					return 0;
				});
			}

			//Process Done
			return this._queryResultData;
		}

		_getItemPriority(itemU, topOverrides) {
			let weight = 0;
			for (let i = 0; i < topOverrides.length; i++) {
				const topOverride = topOverrides[i];
				const index = topOverride.indexOf(itemU);
				if (index !== -1) {
					weight += topOverrides.length - i + topOverride.length - index;
				}
			}
			return weight;
		}

		_getOverrides(dataItem) {
			return dataItem.itemClass.h.map((itemClass, index) => {
				// There is a possibility when the item is not found. This happens when the item is added to the query request and is not part of the visualization data.
				//For example, local filters are added ot the query request but is not part of the visualization data. In this case, the item will be undefined, and we simply ignore it
				let item = this._getMatchingItem(dataItem,index);
				let sortObj = (item) ? item.getSort() : undefined;
				let isSortObjExist = (sortObj && sortObj.custom && sortObj.custom.enabled && sortObj.custom.top && sortObj.custom.top.length) ? true : false;
				let override = [];
				if (item && isSortObjExist && item.getColumnId() === itemClass.u) {
					override = sortObj.custom.top;
				}
				return override;
			});
		}

		/**
		 * If slot contains one data-item, use the data-item id to fetch the item
		 * If slot contains more than one data-item, use the id (ex: categories) to fetch all
		 * 		data-items that exist in the slot. Then loop and get items one at a time,
		 * 		given the index for the dataItem you are on
		 * @return data item, given the Id. If no match (ex: local filters), return undefined
		 */
		_getMatchingItem(dataItem, index) {
			let item;
			if (dataItem.itemClass.h.length > 1) {
				let slots = this.visualizationApi.getSlots().getSlot(dataItem.itemClass.id).getDataItemList();
				item = this.dataItemsMapping[slots[index].getId()];
			} else {
				item = this.dataItemsMapping[dataItem.itemClass.id];
			}
			return item;
		}

		_updateDataItemWithOverrides(dataItem, dataItemIndex, topOverrides) {
			const itemsWithIndex = dataItem.items.map((item, index) => {
				item.originalIndex = index;
				return item;
			});

			itemsWithIndex.sort((itemA, itemB) => {
				let order = 0;
				// loop through the item array, if there's more than one then find the first item that's different, then...
				// order according to their top overrides and default to the natural order determined by their indices in the incoming list
				for (let i = 0; i < itemA.t.length; i++) {
					if (itemA.t[i].u !== itemB.t[i].u) {
						const itemAWeight = this._getItemPriority(itemA.t[i].u, topOverrides);
						const itemBWeight = this._getItemPriority(itemB.t[i].u, topOverrides);
						if (itemAWeight !== itemBWeight) {
							order = itemBWeight - itemAWeight;
						}
						break;
					}
				}
				return order || itemA.originalIndex - itemB.originalIndex;
			});

			//Update the data item items' ref in the data points array
			const refMap = {};
			itemsWithIndex.forEach((itemWithIndex, index) => {
				refMap[itemWithIndex.originalIndex] = index;
			});
			this.data.forEach((dataPoint) => {
				dataPoint.pt[dataItemIndex] = refMap[dataPoint.pt[dataItemIndex]];
			});

			// return with new sort, removing added field "originalIndex"
			return itemsWithIndex.map(itemWithIndex => {
				delete itemWithIndex.originalIndex;
				return itemWithIndex;
			});
		}

		_findIndex(array, predicate) {
			const len = array.length;
			let k = 0;
			while (k < len) {
				let kValue = array[k];
				if (predicate.call(this, kValue, k, array)) {
					return k;
				}
				k++;
			}
			return -1;
		}
	};

});
